/**
 * useChartData Hook
 * Fetches player and team stats for chart visualization
 */
import type { GameData } from '@components/ui/StatsBarChart';
import type { TeamGameData } from '@components/ui/TeamStatsBarChart';
import type { LeagueKey } from '@parlay-picker/shared/league';
import { useCallback } from 'react';

// =============================================================================
// Types
// =============================================================================

export interface PlayerStatsResponse {
  recentGames: GameData[];
  h2hGames?: GameData[];
}

export interface TeamStatsResponse {
  recentGames: TeamGameData[];
}

// =============================================================================
// Hook
// =============================================================================

export function useChartData() {
  /**
   * Fetch team stats for chart display
   * @param period - Optional period for period-specific charts (e.g., 'q1', 'h1')
   */
  const fetchTeamStats = useCallback(
    async (teamName: string, leagueKey: LeagueKey, period?: string): Promise<TeamStatsResponse> => {
      const params = new URLSearchParams({ leagueKey });
      if (period && period !== 'full') params.set('period', period);
      const url = `/api/stats/teams/${encodeURIComponent(teamName)}?${params.toString()}`;
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error('Failed to fetch team stats');
      }
      return await res.json();
    },
    [],
  );

  /**
   * Fetch player stats for chart display (with optional opponent for H2H)
   * @param period - Optional period for period-specific charts (e.g., 'q1')
   */
  const fetchPlayerStats = useCallback(
    async (
      playerName: string,
      leagueKey: LeagueKey,
      opponentAbbr?: string,
      period?: string,
    ): Promise<PlayerStatsResponse> => {
      const params = new URLSearchParams({ leagueKey });
      if (opponentAbbr) params.set('opponent', opponentAbbr);
      if (period && period !== 'full') params.set('period', period);
      const url = `/api/stats/players/${encodeURIComponent(playerName)}?${params.toString()}`;

      const res = await fetch(url);
      if (!res.ok) {
        throw new Error('Failed to fetch player stats');
      }
      return await res.json();
    },
    [],
  );

  return {
    fetchTeamStats,
    fetchPlayerStats,
  };
}
